import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import NonDependentPicker from './NonDependentPicker';

const SCOPES = [
  { id: 'self', title: 'Self', hint: 'Referral letter in your name' },
  { id: 'dependent', title: 'Dependent', hint: 'Spouse, child, parent on your record' },
  { id: 'non_dependent', title: 'Non-dependent', hint: 'Guest / relative not on family tree' },
];

const AFFILIATION_LABELS = {
  faculty_staff_relative: 'Relative to faculty/staff',
  guest: 'Guest',
  project_staff: 'Project staff',
  intern: 'Intern',
  contractor: 'Contractor',
  other: 'Other',
};

export default function PatientInfoFields({
  form, setForm, relationships, affiliations, family, pastNonDependents,
  onPickFamily, onPickNonDependent, onCreateNonDependent, scope, setScope,
}) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const relOptions = scope === 'non_dependent'
    ? (affiliations || Object.keys(AFFILIATION_LABELS))
    : (relationships || []).filter((r) => (scope === 'self' ? r === 'self' : r !== 'self'));

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-heading text-xl text-stone-900">Patient Information</h2>
        <p className="text-sm text-stone-500">Self, dependent family, or a non-dependent guest.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {SCOPES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setScope(s.id)}
            data-testid={`referral-scope-${s.id}`}
            className={`rounded-2xl border px-4 py-3 text-left transition ${
              scope === s.id ? 'border-[var(--brand)] bg-orange-50 shadow-sm' : 'border-stone-200 bg-white'
            }`}
          >
            <p className="font-semibold text-stone-900">{s.title}</p>
            <p className="text-xs text-stone-500">{s.hint}</p>
          </button>
        ))}
      </div>

      {scope === 'dependent' && family?.length > 0 && (
        <div>
          <Label>Select family member</Label>
          <Select onValueChange={onPickFamily}>
            <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Choose linked member" /></SelectTrigger>
            <SelectContent>
              {family.filter((f) => (f.relationship || 'self') !== 'self').map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name} · {f.relationship}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {scope === 'non_dependent' && (
        <NonDependentPicker
          past={pastNonDependents}
          onPick={onPickNonDependent}
          onCreateNew={onCreateNonDependent}
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name" value={form.name} onChange={(v) => set('name', v)} required />
        <div>
          <Label>{scope === 'non_dependent' ? 'Affiliation' : 'Relationship'}</Label>
          <Select value={form.relationship} onValueChange={(v) => set('relationship', v)} disabled={scope === 'self'}>
            <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {relOptions.map((r) => (
                <SelectItem key={r} value={r}>{AFFILIATION_LABELS[r] || r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Field label="Phone" value={form.phone} onChange={(v) => set('phone', v)} required />
        <Field label="Age" value={form.age} onChange={(v) => set('age', v)} type="number" />
        <div>
          <Label>Gender</Label>
          <Select value={form.gender || ''} onValueChange={(v) => set('gender', v)}>
            <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Field label="Date of birth" value={form.dob} onChange={(v) => set('dob', v)} type="date" />
        <Field label="MRN (optional)" value={form.mrn} onChange={(v) => set('mrn', v)} />
        <Field label="Address" value={form.address} onChange={(v) => set('address', v)} />
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = 'text', required }) {
  return (
    <div>
      <Label>{label}{required ? ' *' : ''}</Label>
      <Input className="mt-1 bg-white" type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
